import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToastContext } from '@/contexts/toastContext';
import financePlaidService from '@/services/financePlaid';
import { Paths } from '@/types/routes';
import FinanceXOnboardingComponent from './FinanceXOnboarding.component';

/**
 * Loads the Plaid Link SDK from CDN and opens the Link modal.
 * Reused from Integrations page pattern.
 */
function PlaidLinkOpener({
  linkToken,
  onSuccess,
  onExit,
}: {
  linkToken: string;
  onSuccess: (publicToken: string) => void;
  onExit: () => void;
}): null {
  const openedRef = useRef(false);

  useEffect(() => {
    if (openedRef.current) return;

    const SCRIPT_ID = 'plaid-link-sdk';

    function openPlaidLink(): void {
      const { Plaid } = window as unknown as Record<string, unknown>;
      if (!Plaid) return;
      openedRef.current = true;

      const handler = (Plaid as Record<string, (...args: unknown[]) => unknown>).create({
        token: linkToken,
        onSuccess: (publicToken: unknown) => onSuccess(publicToken as string),
        onExit,
      });
      (handler as Record<string, (...args: unknown[]) => unknown>).open();
    }

    if ((window as unknown as Record<string, unknown>).Plaid) {
      openPlaidLink();
      return;
    }

    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
      script.onload = openPlaidLink;
      document.head.appendChild(script);
    }
  }, [linkToken, onSuccess, onExit]);

  return null;
}

const FinanceXOnboardingContainer = function (): JSX.Element {
  const navigate = useNavigate();
  const toast = useToastContext();
  const [isConnecting, setIsConnecting] = useState(false);
  const [plaidLinkToken, setPlaidLinkToken] = useState<string | null>(null);

  const handleConnectBank = useCallback(async () => {
    setIsConnecting(true);
    try {
      const { linkToken } = await financePlaidService.createLinkToken();
      setPlaidLinkToken(linkToken);
    } catch {
      toast.showErrorFeedback('Failed to start bank connection. Please try again.');
      setIsConnecting(false);
    }
  }, [toast]);

  const handlePlaidSuccess = useCallback(
    async (publicToken: string) => {
      try {
        await financePlaidService.exchangeToken(publicToken);
        navigate(Paths.FINANCEX_BANK_ACCOUNTS);
      } catch {
        toast.showErrorFeedback('Failed to connect bank account. Please try again.');
      } finally {
        setIsConnecting(false);
        setPlaidLinkToken(null);
      }
    },
    [navigate, toast]
  );

  const handlePlaidExit = useCallback(() => {
    setIsConnecting(false);
    setPlaidLinkToken(null);
  }, []);

  const handleImportCSV = useCallback(() => {
    navigate(Paths.FINANCEX_IMPORT);
  }, [navigate]);

  const handleSkip = useCallback(() => {
    navigate(Paths.FINANCEX_TRANSACTIONS);
  }, [navigate]);

  return (
    <>
      <FinanceXOnboardingComponent
        onConnectBank={handleConnectBank}
        onImportCSV={handleImportCSV}
        onSkip={handleSkip}
        isConnecting={isConnecting}
      />
      {plaidLinkToken && (
        <PlaidLinkOpener
          linkToken={plaidLinkToken}
          onSuccess={handlePlaidSuccess}
          onExit={handlePlaidExit}
        />
      )}
    </>
  );
};

export default FinanceXOnboardingContainer;
